import {FormControl, FormField} from 'platform/components';
import {Box, Grid, Heading, Space, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useReadCodelistQuery} from '@dms/api/core';
import {useGetRentalOrderQuery} from '@dms/api/rental';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {MileageInput, openPredefinedNotesDropdown} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId, useRequiredParams} from 'shared/web';

import {FUEL_TANK_STEPS} from '../../../../../constants/FUEL_TANK_STEPS';
import {RentalOrderNodeContext} from '../../../../../constants/RentalOrderNodeContext';
import {RentVehicleFormValues} from '../types/RentVehicleFormValues';

interface ConditionWhenRentingProps extends RequiredTestIdProps {
  control: FormControl<RentVehicleFormValues>;
  formApi: UseFormReturn<RentVehicleFormValues>;
}

export function ConditionWhenRenting(props: ConditionWhenRentingProps) {
  const {id: rentalOrderId} = useRequiredParams();
  const {data: rentalOrder} = useGetRentalOrderQuery({rentalOrderId});
  const vehicleId = rentalOrder?.rentalVehicleId;
  const shouldValidateMileage = isNilOrEmpty(rentalOrder?.rentVehicle?.actualMileage);

  const {
    data: equipmentData,
    isLoading: isLoadingEquipment,
    isError: isEquipmentError,
  } = useReadCodelistQuery({codelistId: 'equipment'});

  const {
    data: rentTypeData,
    isLoading: isLoadingRentType,
    isError: isRentTypeError,
  } = useReadCodelistQuery({codelistId: 'rentType'});

  const rentTypeOptions =
    rentTypeData?.codes?.map((code) => ({
      value: code.codeId,
      label: code.name,
    })) || [];

  const equipmentOptions =
    equipmentData?.codes?.map((code) => ({
      value: code.codeId,
      label: code.name,
    })) || [];

  const conditionNote = useWatch({control: props.control, name: 'conditionNote'});

  return (
    <VStack>
      <Heading size={4}>{i18n.t('page.rentalOrder.labels.conditionWhenRenting')}</Heading>

      <Space vertical={4} />

      <Grid columns={4}>
        <FormField
          control={props.control}
          type="choice"
          isNotClearable
          isDisabled={isRentTypeError}
          isLoading={isLoadingRentType}
          name="rentType"
          isRequired
          options={rentTypeOptions}
          label={i18n.t('page.rentalOrder.labels.rentType')}
          data-testid={suffixTestId('rentType', props)}
        />

        <MileageInput
          control={props.control}
          formApi={props.formApi}
          vehicleId={vehicleId}
          name="actualMileage"
          isRequired
          shouldShowWarning={shouldValidateMileage}
          label={i18n.t('page.rentalOrder.labels.actualMileage')}
          data-testid={suffixTestId('actualMileage', props)}
        />

        <FormField
          control={props.control}
          type="slider"
          name="fuelTank"
          isRequired
          min={0}
          max={1}
          step={0.05}
          nonlinearTicks={FUEL_TANK_STEPS}
          label={i18n.t('page.rentalOrder.labels.fuelTank')}
          data-testid={suffixTestId('fuelTank', props)}
        />
      </Grid>

      <VStack spacing={4}>
        <Box flex={1}>
          <FormField
            control={props.control}
            type="multiChoice"
            name="equipment"
            isDisabled={isEquipmentError}
            isLoading={isLoadingEquipment}
            options={equipmentOptions}
            label={i18n.t('page.rentalOrder.labels.equipment')}
            data-testid={suffixTestId('equipment', props)}
          />
        </Box>
        <Box flex={1}>
          <FormField
            control={props.control}
            type="textarea"
            name="conditionNote"
            label={i18n.t('page.rentalOrder.labels.conditionNote')}
            data-testid={suffixTestId('conditionNote', props)}
            labelActions={[
              {
                title: i18n.t('general.labels.templates'),
                leftIcon: 'content/add_circle',
                onClick: (event) => {
                  openPredefinedNotesDropdown(event, {
                    note: conditionNote ?? null,
                    resource: EntityResourceIds.rentalOrder,
                    context: RentalOrderNodeContext.rentVehicleConditionNote,
                    onPrefill: (text) => {
                      props.formApi.setValue('conditionNote', text);
                    },
                    'data-testid': suffixTestId('conditionNoteTemplates', props),
                  });
                },
              },
            ]}
          />
        </Box>
      </VStack>
    </VStack>
  );
}
